import { msg } from '@lit/localize';
import { ExerciseLogEntry, ExerciseLogEntryv2, TrainingDayLogv2, TrainingDayLogv3 } from "../models/traininghistorymodel";
import {
  BlockModel,
  CombinationKind,
  defaultColorFor,
  ExerciseModelv2,
  ExerciseTypesEnum,
  SlotModel,
  stablePartitionBy,
  stringifyCadence,
  TrainingDayModel,
  TrainingDayModelv2,
  TrainingDayModelv3,
  WorkoutModelDBv2,
  WorkoutModelDBv3,
} from "../models/trainingmodel";
import { WorkoutModelDBv3Zod, TrainingDayLogv3Zod } from "../models/trainingmodel-v3-schemas";
import { tfpapi } from "./trainfitpro-apiservice";
import { seriesParser } from "./utilities";

type BasePlanType = NonNullable<Awaited<ReturnType<typeof tfpapi.trainer.workouts.get>>['data']>['result'][number]

/**
 * Thrown when a read path is handed a payload version it cannot render.
 * Per the v3 rollout ordering rule (design §4.4): deployed v2 builds must
 * REFUSE version >= 3 blobs (render the "update the app" surface) instead of
 * half-parsing them — a v2 client reading a v3 plan would crash on
 * `exercises.map` of undefined. Callers catch this and show the update UI.
 */
export class UnsupportedDataVersionError extends Error {
  constructor(public readonly kind: 'plan' | 'log', public readonly version: number) {
    super(`Unsupported ${kind} data version ${version}: this build cannot read version ${version} payloads`);
    this.name = 'UnsupportedDataVersionError';
  }
}

export function migrationToWorkoutModelDBv2(
  workout: BasePlanType & Record<string, unknown>
): WorkoutModelDBv2 {
  if (Number(workout.version ?? 1) >= 3) {
    throw new UnsupportedDataVersionError('plan', Number(workout.version));
  }
  if (workout.version == 2) {
    const result: WorkoutModelDBv2 = {
      ...workout,
      userUUID: workout.userUUID ?? undefined,
      startDate: new Date(workout.startDate!),
      endDate: new Date(workout.endDate!),
      published: workout.published ? new Date(workout.published) : undefined,
      workouts: workout.workouts as TrainingDayModelv2[],
    };
    return result;
  } else {
    return planRawToV2(workout);
  }
}

// Shared v1→v2 normalization (string series → SeriesModel[]). Used by both the
// v2 and the v3 read paths so there is exactly ONE implementation.
function planRawToV2(workout: BasePlanType & Record<string, unknown>): WorkoutModelDBv2 {
  const result: WorkoutModelDBv2 = {
    ...workout,
    version: 2,
    userUUID: workout.userUUID ?? undefined,
    startDate: new Date(workout.startDate!),
    endDate: new Date(workout.endDate!),
    published: undefined,
    workouts: (workout.workouts as TrainingDayModel[]).map((trainingDay) => {
      const result: TrainingDayModelv2 = {
        name: trainingDay.name,
        exercises: trainingDay.exercises.map((ex) => {
          const r: ExerciseModelv2 = {
            ...ex,
            execution: {
              ...ex.execution,
              series: seriesParser(ex.execution.series.join(' ')),
            }
          };
          return r;
        }),
      }
      return result;
    }),
  };
  return result;
}

// ============================================================================
// v3 read-time migration (design of record:
// new-features/exercise_combination_data_design_v3_1.md §4.2/§4.3).
//
// Read-time wrap + write-through: upgrades happen in memory at read; the stored
// blob upgrades on the owner's next save. The wrap is PURE (no invention): the
// day's exercises become solo 1-slot blocks grouped into phases by a STABLE
// partition on type. The log transform below must use the identical
// stablePartitionBy (shared from trainingmodel.ts) or carry-forward lands on
// the wrong block — this pairing is the migration window's positional contract.
// ============================================================================

/** Display name the v2 editor header already shows for a derived type bucket. */
function localizedTypeName(type: ExerciseTypesEnum): string {
  switch (type) {
    case ExerciseTypesEnum.mobility:
      return msg('Mobility');
    case ExerciseTypesEnum.stretching:
      return msg('Stretching');
    case ExerciseTypesEnum.cardio:
      return msg('Cardio');
    case ExerciseTypesEnum.weight:
    default:
      return msg('Weight');
  }
}

/**
 * Pure solo wrap (design §4.2): today's exercise becomes the 1-slot block.
 * The old per-exercise `pause` was the rest between ITS sets → inter-set break
 * (`roundPause`); the v3 slot `pause` ("Inter-exercise break") is authored new.
 */
function wrapSolo(e: ExerciseModelv2): BlockModel {
  const slot: SlotModel = {
    exercise: e.name,
    type: e.type,
    media: e.media,
    execution: { series: e.execution.series },
    ...(e.observations !== undefined ? { observations: e.observations } : {}),
    ...(stringifyCadence(e.execution.cadence) !== undefined ? { cadence: stringifyCadence(e.execution.cadence) } : {}),
  };
  return {
    kind: CombinationKind.solo,
    execution: { rounds: 1, ...(e.execution.pause !== undefined ? { roundPause: e.execution.pause } : {}) },
    slots: [slot],
  };
}

/**
 * Upgrade a plan blob (version 1, 2, or 3) to WorkoutModelDBv3.
 * version 3 → shape-validated passthrough; 2 → wrap; 1 → v2 normalize, then wrap.
 * `validate` is a test seam (defaults on): wrap output is built to satisfy the
 * discriminated unions, so a wrap that fails them is a transform bug, not data.
 */
export function migrationToWorkoutModelDBv3(
  workout: BasePlanType & Record<string, unknown>,
  validate = true,
): WorkoutModelDBv3 {
  const version = Number(workout.version ?? 1);
  if (version > 3) {
    throw new UnsupportedDataVersionError('plan', version);
  }
  if (version === 3) {
    const candidate = {
      ...workout,
      userUUID: workout.userUUID ?? undefined,
      startDate: new Date(workout.startDate!),
      endDate: new Date(workout.endDate!),
      published: workout.published ? new Date(workout.published) : undefined,
    };
    return validate
      ? WorkoutModelDBv3Zod.parse(candidate)
      : candidate as unknown as WorkoutModelDBv3;
  }
  const v2 = version === 2 ? migrationToWorkoutModelDBv2(workout) : planRawToV2(workout);
  const result: WorkoutModelDBv3 = {
    ...v2,
    version: 3,
    workouts: v2.workouts.map((day): TrainingDayModelv3 => ({
      name: day.name,
      phases: stablePartitionBy(day.exercises, (e) => e.type).map(([type, exs]) => ({
        name: localizedTypeName(type),
        color: defaultColorFor(type),
        blocks: exs.map(wrapSolo),
      })),
    })),
  };
  return validate ? WorkoutModelDBv3Zod.parse(result) : result;
}

type BaseLogType = NonNullable<Awaited<ReturnType<typeof tfpapi.journal.workoutlog.get>>['data']>['result'][number]

export function migrationToTrainingDayLogv2(
  log: BaseLogType & Record<string, unknown>
): TrainingDayLogv2 {
  if (Number(log.version ?? 1) >= 3) {
    throw new UnsupportedDataVersionError('log', Number(log.version));
  }
  if (log.version == 2) {
    return {
      ...log,
      date: new Date(log.date ?? ''),
      log: log.log as unknown as ExerciseLogEntryv2[],
    };
  } else {
    return logRawToV2(log);
  }
}

// Shared v1→v2 log normalization (string series → SeriesModel[]).
function logRawToV2(log: BaseLogType & Record<string, unknown>): TrainingDayLogv2 {
  const result: TrainingDayLogv2 = {
    ...log,
    version: 2,
    date: new Date(log.date ?? ''),
    log: (log.log as unknown as ExerciseLogEntry[]).map((log) => {
      const result: ExerciseLogEntryv2 = {
        ...log,
        execution: {
          ...log.execution,
          series: seriesParser(log.execution.series.join(' ')),
        }
      };
      return result;
    }),
  };
  return result;
}

/**
 * Upgrade a log blob (version 1, 2, or 3) to TrainingDayLogv3.
 *
 * POSITIONAL CONTRACT (§4.3): each v2 entry becomes a solo BlockLogEntry and the
 * log moves by the IDENTICAL stablePartitionBy(entry.type) the plan transform
 * applies — equal group order + equal within-group order on both sides, so
 * flattened block index i still meets log[i] after regrouping.
 */
export function migrationToTrainingDayLogv3(
  log: BaseLogType & Record<string, unknown>,
  validate = true,
): TrainingDayLogv3 {
  const version = Number(log.version ?? 1);
  if (version > 3) {
    throw new UnsupportedDataVersionError('log', version);
  }
  if (version === 3) {
    const candidate = {
      ...log,
      date: new Date(log.date ?? ''),
    };
    return validate
      ? TrainingDayLogv3Zod.parse(candidate)
      : candidate as unknown as TrainingDayLogv3;
  }
  const v2 = version === 2 ? migrationToTrainingDayLogv2(log) : logRawToV2(log);
  const result = {
    ...v2,
    version: 3 as const,
    log: stablePartitionBy(v2.log, (e) => e.type).flatMap(([, entries]) =>
      entries.map((e) => ({
        kind: CombinationKind.solo,
        slots: [e],
        ...(e.notes !== undefined ? { notes: e.notes } : {}),
      })),
    ),
  };
  return validate ? TrainingDayLogv3Zod.parse(result) : result as unknown as TrainingDayLogv3;
}
